import { Component, ReactNode, ErrorInfo } from "react";
import { SkyButton } from "@skypointcloud/skypoint-ui";
import { AlertCircle, RefreshCw, Home, Copy } from "lucide-react";
import { ROUTES } from "./constant";
import { authService as auth } from "../../utils/authService";
import { logger } from "../../logger-init";
import { v4 as uuidv4 } from "uuid";

interface ErrorBoundaryProps {
  children: ReactNode;
  fallbackTitle?: string;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
  errorId: string | null;
}

class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null, errorId: null };
  }

  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    return { hasError: true, error, errorId: uuidv4() };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    this.setState({ errorInfo });

    logger.error("React component error caught by ErrorBoundary", error, {
      component: "ErrorBoundary",
      componentStack: errorInfo?.componentStack,
      errorBoundary: true,
      errorId: this.state.errorId,
      errorName: error.name,
      errorMessage: error.message,
    });
  }

  handleReload = (): void => {
    window.location.href = ROUTES.HOME + auth.getQueryString();
  };

  handleRetry = (): void => {
    this.setState({ hasError: false, error: null, errorInfo: null, errorId: null });
  };

  handleCopyError = (): void => {
    const { error, errorInfo, errorId } = this.state;
    const errorText = `Error ID: ${errorId}\nError: ${error?.name}: ${error?.message}\nStack: ${error?.stack}\nComponent Stack: ${errorInfo?.componentStack}`;
    navigator.clipboard.writeText(errorText);
  };

  render(): ReactNode {
    const { hasError, error, errorId } = this.state;
    const { fallbackTitle = "Something went wrong" } = this.props;
    const isDev = import.meta.env.DEV;

    if (hasError) {
      return (
        <div className="flex flex-col justify-center items-center h-full min-h-[400px] w-full gap-4 p-8 text-center bg-white dark:bg-gray-900">
          <AlertCircle className="w-16 h-16 text-red-500" />
          <div className="flex flex-col gap-2">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
              {fallbackTitle}
            </h2>
            <p className="text-sm text-gray-500 dark:text-gray-400 max-w-[500px]">
              We encountered an unexpected error. You can try again or return to the home page.
              {errorId && <span className="block mt-2 font-mono text-xs">Error ID: {errorId}</span>}
            </p>
          </div>

          {isDev && error && (
            <div className="mt-4 p-4 bg-red-50 rounded-lg max-w-[600px] text-left border border-red-300">
              <p className="text-sm font-semibold text-red-600">
                {error.name}: {error.message}
              </p>
              {error.stack && (
                <pre className="mt-2 text-[11px] text-red-900 whitespace-pre-wrap break-words max-h-[200px] overflow-auto">
                  {error.stack}
                </pre>
              )}
            </div>
          )}

          <div className="flex gap-3 mt-4">
            <SkyButton
              appearance="primary"
              icon={RefreshCw}
              onClick={this.handleRetry}
            >
              Try again
            </SkyButton>
            <SkyButton
              appearance="secondary"
              icon={Home}
              onClick={this.handleReload}
            >
              Go home
            </SkyButton>
            {isDev && (
              <SkyButton
                appearance="subtle"
                icon={Copy}
                onClick={this.handleCopyError}
              >
                Copy error
              </SkyButton>
            )}
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
